import type { JsonValue, LiveLevelUp, LiveLevelUpChoice } from "../../types/generated/live";
import type { IconName } from "../ui/icon";
import { parseTyped } from "../ui/typed_number";
import { type Dict, interpolate } from "./labels";

// The most one level's PV can be (`Advancement::Writer::MAX_HP`).
export const MAX_HP = 999;

// The PV typed for a level: whole, from 1 to the most the server takes. A decimal drops its fraction and a number past the most
// becomes it, which the result card shows instead of a refusal at the save; below 1 or not a number, nothing.
export const typedHp = (text: string): number | null => {
	const typed = parseTyped(text, { max: MAX_HP });
	return typed !== null && typed >= 1 ? typed : null;
};

// What the player is filling in: the die rolled (or typed) for the level's PV, and one answer per choice key, in the shape
// the ops take (a weapon or an option as a string, damage types and talent points as lists of tags and talent keys, the
// grimoire's new spells as a list of spell keys).
export type Answer = string | string[];
export type Draft = { hp: number | null; answers: Record<string, Answer> };

// "up" raises the level; "choices" answers what an earlier level left pending.
export type Mode = "up" | "choices";

// What the popup can ask. The race's choices (`race.<id>`) stay with the race step, the first memorized spells with the
// spell picker, and the PV have a block of their own.
const ASKED = new Set<LiveLevelUpChoice["kind"]>(["mastery_weapon", "mastery_damage_types", "talent_points", "select_one", "text_field", "grimoire"]);

export const blocks = (data: LiveLevelUp): LiveLevelUpChoice[] => data.step.owed.filter((choice) => ASKED.has(choice.kind));

const text = (value: JsonValue | undefined) => (typeof value === "string" ? value : undefined);
const list = (value: JsonValue | undefined) => (Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : undefined);
const blank = (answer: Answer | undefined) => answer === undefined || (typeof answer === "string" ? answer.trim() === "" : answer.length === 0);

// What the character already holds for the level (a question left pending, an earlier PV) starts the draft.
export function initialDraft(data: LiveLevelUp): Draft {
	const answers: Record<string, Answer> = {};
	for (const choice of blocks(data)) {
		if (choice.kind === "grimoire") continue;
		const stored = choice.kind === "mastery_damage_types" || choice.kind === "talent_points" ? list(data.answers[choice.key]) : text(data.answers[choice.key]);
		if (!blank(stored)) answers[choice.key] = stored as Answer;
	}
	const hp = data.answers[`hp.${data.level}`];
	return { hp: typeof hp === "number" && hp >= 1 ? hp : null, answers };
}

// --- PV

export const rolledDie = (data: LiveLevelUp): number | null => data.step.hp.die;

// The PV the level adds: the die and the CON modifier (never below 1) plus the class's and race's bonus, or the fixed value;
// a bonus that starts at the level also counts for the levels already taken (`catch_up`).
export function hpGain(data: LiveLevelUp, roll: number | null, constitution: number): number | null {
	const { die, fixed, value, bonus, catch_up: catchUp, constitution: counts } = data.step.hp;
	const catch_up = catchUp ?? 0;
	if (die == null) {
		const level = value ?? (fixed == null ? null : fixed + bonus);
		return level === null ? null : level + catch_up;
	}
	if (roll == null) return null;
	return Math.max(1, roll + (counts ? constitution : 0)) + bonus + catch_up;
}

// --- talent points

export type TalentRow = { key: string; title: string; description: string; base: number; points: number; total: number; over: boolean };

export const pointsOf = (draft: Draft, choice: LiveLevelUpChoice, talent: string): number =>
	((draft.answers[choice.key] as string[] | undefined) ?? []).filter((key) => key === talent).length;

// Every talent block of the level on the same ability: a level 1 thief has one for the DES modifier and one for the level,
// and a talent's score takes the points of all of them.
const siblings = (data: LiveLevelUp, choice: LiveLevelUpChoice) =>
	blocks(data).filter((one) => one.kind === "talent_points" && one.ability_id === choice.ability_id);
const count = (keys: string[] | undefined, talent: string) => (keys ?? []).filter((key) => key === talent).length;

// The score each talent has without the level's points (the stored ones are already in it), and what it would be with
// every block's draft. `points` is this block's own.
export function talentRows(data: LiveLevelUp, draft: Draft, choice: LiveLevelUpChoice): TalentRow[] {
	const others = siblings(data, choice);
	return data.talents
		.filter((talent) => talent.ability_id === choice.ability_id)
		.map((talent) => {
			const base = talent.score - others.reduce((sum, one) => sum + count(list(data.answers[one.key]), talent.key), 0);
			const total = base + others.reduce((sum, one) => sum + pointsOf(draft, one, talent.key), 0);
			const points = pointsOf(draft, choice, talent.key);
			return { key: talent.key, title: talent.title, description: talent.description, base, points, total, over: points > 1 || total > talent.max };
		});
}

export function setTalent(data: LiveLevelUp, draft: Draft, choice: LiveLevelUpChoice, talent: string, points: number): Draft {
	const next = Math.max(0, points);
	const picks = data.talents
		.filter((one) => one.ability_id === choice.ability_id)
		.flatMap((one) => Array(one.key === talent ? next : pointsOf(draft, choice, one.key)).fill(one.key));
	return { ...draft, answers: { ...draft.answers, [choice.key]: picks } };
}

// "Pontos dados: 3 de 2": over the book's count, or two in a talent, or above the maximum, is red and allowed.
export function talentCounter(data: LiveLevelUp, draft: Draft, choice: LiveLevelUpChoice) {
	const given = ((draft.answers[choice.key] as string[] | undefined) ?? []).length;
	return {
		given,
		of: choice.count,
		over: given > choice.count || talentRows(data, draft, choice).some((row) => row.over),
		max: data.talents.find((one) => one.ability_id === choice.ability_id)?.max ?? null,
	};
}

// --- mastery

// The weapons the character already masters, from the mastery ability's line on the sheet, without this choice's own stored pick.
export function masteredWeapons(known: string | null | undefined, stored: string | undefined): string[] {
	const names = (known ?? "").split(", ").filter(Boolean);
	const own = names.indexOf(stored ?? "");
	return own < 0 ? names : names.filter((_, index) => index !== own);
}

// --- grimoire

export const candidates = (data: LiveLevelUp, choice: LiveLevelUpChoice) => data.grimoires.find((one) => `grimoire.${one.circle}` === choice.key)?.spells ?? [];

// --- what is still open

export type Open = { kind: LiveLevelUpChoice["kind"]; name: string | null; count: number };

export function missing(choice: LiveLevelUpChoice, answer: Answer | undefined): number {
	if (choice.optional || choice.kind === "grimoire") return 0;
	if (choice.kind === "mastery_damage_types" || choice.kind === "talent_points") return Math.max(0, Math.max(1, choice.count) - (answer?.length ?? 0));
	return blank(answer) ? 1 : 0;
}

// Never blocks: the level goes up (or the answers save) and these stay pending on the sheet.
export function openItems(data: LiveLevelUp, draft: Draft): Open[] {
	const open: Open[] = [];
	if (rolledDie(data) != null && draft.hp == null) open.push({ kind: "hp", name: null, count: 1 });
	for (const choice of blocks(data)) {
		const count = missing(choice, draft.answers[choice.key]);
		if (count > 0) open.push({ kind: choice.kind, name: choice.name, count });
	}
	return open;
}

const openWord = (item: Open, words: Dict): string => {
	if (item.kind === "talent_points") return interpolate(words[item.count === 1 ? "open_talent_points_one" : "open_talent_points_other"], { count: item.count });
	if (item.kind === "select_one" || item.kind === "text_field") return interpolate(words[`open_${item.kind}`], { name: item.name ?? "" });
	return words[`open_${item.kind}`];
};

export const joined = (items: string[], words: Dict): string =>
	items.length < 2 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")}${words.and}${items[items.length - 1]}`;

export const openList = (open: Open[], words: Dict): string =>
	joined(
		open.map((item) => openWord(item, words)),
		words,
	);

export function footer(open: Open[], words: Dict, mode: Mode): { ready: boolean; text: string } {
	if (open.length === 0) return { ready: true, text: words.ready };
	return { ready: false, text: interpolate(words[mode === "up" ? "open_up" : "open_choices"], { items: openList(open, words) }) };
}

// --- what changes

export type Gain = { icon: IconName; title: string; text: string };

const signed = (n: number) => (n >= 0 ? `+${n}` : String(n));

const moved = (words: Dict, to: string | number, from: string | number | null) =>
	from == null ? interpolate(words.now, { value: to }) : interpolate(words.moved, { from, to });

export function gains(data: LiveLevelUp, words: Dict): Gain[] {
	const { step } = data;
	const found: Gain[] = [];
	if (step.ba_changed)
		found.push({ icon: "weaponMastery", title: words.ba, text: moved(words, signed(step.ba), step.previous_ba == null ? null : signed(step.previous_ba)) });
	if (step.jp_changed) found.push({ icon: "weaponMastery", title: words.jp, text: moved(words, step.jp, step.previous_jp) });
	for (const circle of step.new_circles) {
		const slot = step.slots.find((one) => one.circle === circle);
		found.push({ icon: "spellBook", title: interpolate(words.circle_opens, { circle }), text: slot ? interpolate(words.per_day, { total: slot.total }) : "" });
	}
	const open = step.slots.filter((slot) => !step.new_circles.includes(slot.circle));
	if (open.length > 0) {
		found.push({
			icon: "spellBook",
			title: words.slots,
			text: open.map((slot) => interpolate(words.slot, { circle: slot.circle, total: slot.total })).join(", "),
		});
	}
	if (step.rituals.length > 0) {
		found.push({
			icon: "spellBook",
			title: words.rituals,
			text: step.rituals.map((ritual) => interpolate(words.slot, { circle: ritual.circle, total: ritual.uses })).join(", "),
		});
	}
	for (const ability of step.abilities) found.push({ icon: "sparkles", title: words.new_ability, text: ability.name });
	for (const one of step.texts) found.push({ icon: "sparkles", title: interpolate(words.titled, { name: one.name }), text: one.text.trim() });
	for (const figure of step.figures) {
		const change = figure.from == null || figure.to == null ? "" : interpolate(words.from_to_values, { from: figure.from, to: figure.to });
		found.push({
			icon: "sparkles",
			title: interpolate(words.titled, { name: figure.name }),
			text: `${interpolate(words[`figure_${figure.key}`] ?? words.figure, { to: figure.to ?? 0 })}${change}.`,
		});
	}
	return found;
}

// BA and JP that stay: "BA +3 e JP 8 não mudam neste nível."
export function unchanged(data: LiveLevelUp, words: Dict): string {
	const { step } = data;
	const parts = [
		!step.ba_changed && interpolate(words.same_ba, { value: signed(step.ba) }),
		!step.jp_changed && interpolate(words.same_jp, { value: step.jp }),
	].filter(Boolean) as string[];
	if (parts.length === 0) return "";
	return interpolate(words[parts.length === 2 ? "same_both" : "same_one"], { items: parts.join(words.and_plain), ba: signed(step.ba), jp: step.jp });
}

// --- notes the book makes and the app does not enforce

export type Note = { kind: "xp_short" | "alignment_not_listed" | "race_locked"; text: string };

// The notes that do not depend on what is picked. The XP shortfall only speaks when raising the level.
export function notes(data: LiveLevelUp, words: Dict, mode: Mode, number: (n: number) => string, alignment?: (key: string) => string): Note[] {
	const found: Note[] = [];
	if (mode === "up" && data.experience.missing > 0) {
		found.push({
			kind: "xp_short",
			text: interpolate(words.xp_short, {
				missing: number(data.experience.missing),
				points: number(data.experience.points),
				minimum: number(data.experience.minimum),
			}),
		});
	}
	for (const note of data.notes) {
		if (note.kind === "alignment_not_listed")
			found.push({ kind: note.kind, text: interpolate(words.alignment_not_listed, { alignment: alignment?.(note.subject ?? "") ?? note.subject ?? "" }) });
		if (note.kind === "race_locked") found.push({ kind: note.kind, text: words.race_locked });
	}
	return found;
}

// --- what is sent

// Whether two answers say the same: texts trimmed, lists as sets of picks in any order, and every blank alike.
const same = (a: Answer | undefined, b: Answer | undefined): boolean => {
	if (blank(a) || blank(b)) return blank(a) && blank(b);
	if (typeof a === "string" || typeof b === "string") return typeof a === "string" && typeof b === "string" && a.trim() === b.trim();
	return [...a].sort().join("\n") === [...b].sort().join("\n");
};

// What was edited since the popup opened, for the write to merge: a block the player left alone is not sent, so it cannot
// revert what another editor changed meanwhile. A stored answer taken back to nothing is sent as the writer's empty answer
// (it removes the key); the grimoire sends only the spells newly ticked, since it never unticks.
export function answersOf(data: LiveLevelUp, draft: Draft, baseline: Draft): Record<string, Answer> {
	const sent: Record<string, Answer> = {};
	for (const choice of blocks(data)) {
		const answer = draft.answers[choice.key];
		const was = baseline.answers[choice.key];
		if (same(answer, was)) continue;
		if (blank(answer)) sent[choice.key] = typeof was === "string" ? "" : [];
		else sent[choice.key] = typeof answer === "string" ? answer.trim() : answer;
	}
	return sent;
}

// The PV to send: null leaves the stored roll alone, "" takes it back.
const edited = (draft: Draft, baseline: Draft) => (draft.hp === baseline.hp ? null : (draft.hp ?? ""));

export const levelUpBody = (data: LiveLevelUp, draft: Draft, baseline: Draft) => ({
	op: "level_up",
	to_level: data.level,
	hp: edited(draft, baseline),
	answers: answersOf(data, draft, baseline),
});
export const choicesBody = (data: LiveLevelUp, draft: Draft, baseline: Draft) => ({
	op: "level_choices",
	level: data.level,
	hp: edited(draft, baseline),
	answers: answersOf(data, draft, baseline),
});

export const toggled = (list: string[], entry: string): string[] => (list.includes(entry) ? list.filter((one) => one !== entry) : [...list, entry]);

// --- what was done, for the success state

export function summary(data: LiveLevelUp, draft: Draft, words: Dict, nameOf: (tag: string) => string): string[] {
	const lines: string[] = [];
	for (const choice of blocks(data)) {
		const answer = draft.answers[choice.key];
		if (blank(answer)) continue;
		if (choice.kind === "mastery_weapon") lines.push(interpolate(words.done_mastery, { weapon: answer as string }));
		else if (choice.kind === "mastery_damage_types") lines.push(interpolate(words.done_groups, { list: (answer as string[]).map(nameOf).join(", ") }));
		else if (choice.kind === "talent_points") {
			const rows = talentRows(data, draft, choice).filter((row) => row.points > 0);
			lines.push(interpolate(words.done_talents, { list: rows.map((row) => `+${row.points} ${row.title}`).join(", ") }));
		} else if (choice.kind === "grimoire") {
			const names = candidates(data, choice)
				.filter((spell) => (answer as string[]).includes(spell.key))
				.map((spell) => spell.name);
			lines.push(interpolate(words.done_grimoire, { list: names.join(", ") }));
		} else lines.push(interpolate(words.done_answer, { name: choice.name ?? "", value: answer as string }));
	}
	return lines;
}

// --- the sheet's pending band

// What `derived.pending` carries per item; `name` is the ability's name or the question's prompt.
export type PendingItem = { level: number; key: string; kind: LiveLevelUpChoice["kind"]; missing: number; name?: string | null };

// The race's choices have no popup yet: they link to the race step.
export const isRace = (item: PendingItem): boolean => item.kind === "bonus_jp" || item.kind === "variable_construction";

export function pendingText(item: PendingItem, words: Dict): string {
	const name = item.name ?? "";
	if (item.kind === "talent_points")
		return interpolate(words[item.missing === 1 ? "pending_talent_points_one" : "pending_talent_points_other"], { count: item.missing, level: item.level });
	return interpolate(words[`pending_${item.kind}`] ?? words.pending_other, { level: item.level, name });
}

// The earliest level the popup can resolve, or null when only the race's choices are left.
export const firstPendingLevel = (items: PendingItem[]): number | null =>
	items.filter((item) => !isRace(item)).reduce<number | null>((first, item) => (first === null || item.level < first ? item.level : first), null);

export const pendingList = (items: PendingItem[], words: Dict): string => items.map((item) => pendingText(item, words)).join("; ");
